import type {
    DailyDayPointDto,
    DailyExecutiveSeriesCurrencyDto,
    DailyExecutiveSeriesDto,
} from "@/features/daily/types/daily.types";
import { dailyFormat } from "@/features/daily/components/command/command-view-model";
import { dailyDates } from "@/features/daily/components/daily-dates";
import type { DeltaDirection } from "@/features/daily/components/stat-tile-view-model";

export type SeriesMeasure = "volume" | "revenue";

export type SeriesDayVm = {
    index: number;
    date: string;
    label: string;
    partial: boolean;
    current: number;
    /** B18 (D136): the current day's amount split by type (adds up to `current`). */
    currentByType: { order: number; deposit: number };
    /** `null` when the reference period has no such day (a 30-day month vs 31). Never a zero. */
    reference: number | null;
    referenceDate: string | null;
    cumulativeCurrent: number;
    cumulativeReference: number | null;
};

export type SeriesCurrencyVm = {
    currencyKey: string;
    currencyLabel: string;
    days: SeriesDayVm[];
    /** Compact («39,5k €»): the totals share one line under the chart. */
    totalLabel: string;
    referenceTotalLabel: string;
    /** Signed change of the plotted measure; a dash when the reference is absent or zero. */
    variationLabel: string;
    variationTone: "good" | "critical" | "neutral";
    variationDirection: DeltaDirection;
    windowLabel: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

/** D24: the cache stores `unknown`; the page proves the shape instead of casting. */
export const isExecutiveSeriesDto = (value: unknown): value is DailyExecutiveSeriesDto =>
    isRecord(value) && isRecord(value.envelope) && isRecord(value.referenceWindow)
    && Array.isArray(value.byCurrency) && Array.isArray(value.bestDays);

const currencyKeyOf = (currency: string | null): string => currency ?? "";

const byTypeOf = (point: DailyDayPointDto, measure: SeriesMeasure): { order: number; deposit: number } =>
    measure === "volume" ? point.volumeByType : point.revenueByType;

const valueOf = (point: DailyDayPointDto, measure: SeriesMeasure): number =>
    measure === "volume" ? point.volume : point.revenue;

/** D130: unsigned, the arrow beside it gives the direction — «26.7%». */
const variationPercent = (current: number, reference: number): string | null =>
    reference === 0 ? null : dailyFormat.kpiDelta(((current - reference) / Math.abs(reference)) * 100, "%");

/**
 * One currency's daily points aligned by position against the reference period.
 * Days that have not happened are simply absent (B1): nothing here fills them with
 * zeros, and a reference day that does not exist stays `null`.
 */
const daysOf = (entry: DailyExecutiveSeriesCurrencyDto, measure: SeriesMeasure, today: string): SeriesDayVm[] => {
    let runningCurrent = 0;
    let runningReference: number | null = 0;
    return entry.current.map((point, index) => {
        const referencePoint = entry.reference[index];
        const current = valueOf(point, measure);
        const reference = referencePoint === undefined ? null : valueOf(referencePoint, measure);
        runningCurrent += current;
        runningReference = runningReference === null || reference === null ? null : runningReference + reference;
        return {
            index,
            date: point.date,
            label: dailyDates.shortLabel(point.date),
            partial: point.date === today,
            current,
            currentByType: byTypeOf(point, measure),
            reference,
            referenceDate: referencePoint === undefined ? null : referencePoint.date,
            cumulativeCurrent: runningCurrent,
            cumulativeReference: runningReference,
        };
    });
};

const rangeLabel = (from: string | undefined, to: string | undefined): string =>
    from === undefined || to === undefined ? dailyFormat.absent : `${dailyDates.shortLabel(from)}–${dailyDates.shortLabel(to)}`;

export const seriesViewModel = {
    /** The Madrid calendar day the server evaluated this answer on — its own "today". */
    today: (dto: DailyExecutiveSeriesDto): string => dailyDates.madridDateOf(dto.envelope.evaluatedAt),

    /**
     * D103: every figure below belongs to ONE currency. Volume and revenue are never
     * summed across currencies, and a comparison without a base says so.
     */
    currency: (dto: DailyExecutiveSeriesDto, currencyKey: string, measure: SeriesMeasure): SeriesCurrencyVm | null => {
        const entry = dto.byCurrency.find((candidate) => currencyKeyOf(candidate.currency) === currencyKey)
            ?? dto.byCurrency[0];
        if (entry === undefined) return null;
        const today = seriesViewModel.today(dto);
        const currency = entry.currency;
        const last = entry.current[entry.current.length - 1];
        const first = entry.current[0];
        const partial = last !== undefined && last.date === today;
        const cut = partial ? ` · corte ${dailyFormat.clock(dto.envelope.evaluatedAt)}` : " · días completos";
        const total = entry.currentTotal[measure];
        const referenceTotal = entry.referenceTotal[measure];
        const hasReference = entry.reference.length > 0;

        return {
            currencyKey: currencyKeyOf(currency),
            currencyLabel: currency ?? "Moneda no registrada",
            days: daysOf(entry, measure, today),
            totalLabel: dailyFormat.compactMoney(total, currency),
            referenceTotalLabel: hasReference ? dailyFormat.compactMoney(referenceTotal, currency) : "no disponible",
            variationLabel: hasReference ? variationPercent(total, referenceTotal) ?? dailyFormat.absent : dailyFormat.absent,
            variationTone: !hasReference || referenceTotal === 0 || total === referenceTotal
                ? "neutral"
                : total > referenceTotal ? "good" : "critical",
            variationDirection: !hasReference || total === referenceTotal ? "flat" : total > referenceTotal ? "up" : "down",
            windowLabel: `${rangeLabel(first?.date, last?.date)}${cut}`,
        };
    },
};
